import React from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  useDisclosure,
  Divider,
} from '@chakra-ui/react';
import { FiInfo, FiArrowRightCircle } from 'react-icons/fi';
import { useOptionalLanguage } from '../i18n';
import { useRegisterPageHelp } from './workspace/WorkspaceAssistantContext';

/**
 * Lightweight page-level help trigger with a polished modal.
 */
const PageHelp = ({ title, description, steps = [], align = 'flex-end' }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const zh = useOptionalLanguage()?.lang === 'zh';
  // Hands this page's help to the workspace assistant (no-op outside the workspace).
  useRegisterPageHelp({ title, description, steps });

  return (
    <Box display="flex" justifyContent={align} mb={4}>
      <Button
        onClick={onOpen}
        leftIcon={<FiInfo />}
        variant="outline"
        size="sm"
        colorScheme="brand"
        borderRadius="full"
        shadow="sm"
      >
        {zh ? '使用说明' : 'How to use'}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Badge colorScheme="brand" borderRadius="full" px={2}>
                {zh ? '指南' : 'Guide'}
              </Badge>
              <Text>{title}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {description && (
              <Text color="gray.700" mb={4}>
                {description}
              </Text>
            )}
            <VStack align="stretch" spacing={3}>
              {steps.map((step, idx) => (
                <Box
                  key={step.title || idx}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="gray.50"
                >
                  <HStack spacing={3} align="start">
                    <Icon as={FiArrowRightCircle} color="brand.500" boxSize={5} mt="2px" />
                    <VStack align="start" spacing={1}>
                      {step.title && (
                        <Text fontWeight="semibold" color="gray.800">
                          {step.title}
                        </Text>
                      )}
                      <Text color="gray.700" lineHeight="1.5">
                        {step.detail}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button onClick={onClose} colorScheme="brand">
              {zh ? '知道了' : 'Got it'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PageHelp;
